import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Sparkle, ArrowRight, Check, Lightbulb, Clock, FloppyDisk,
  CurrencyInr, Users, Camera, FilmStrip, MusicNote, ListNumbers,
} from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "../../lib/api";
import StepByStepComposer from "./StepByStepComposer";
import { assetUrl } from "../../lib/assetUrl";

/* The redesigned multi-step Storyboard composer.
   Step 1: Script  →  Step 2: Image + Cast  →  Step 3: Video  →  Step 4: Timeline + Schedule.
   v1 ships the wizard skeleton — drag-drop reorder + scene exclusion + per-step budget caps.
   Iter-38: added a "Step-by-step" mode that generates+reviews each phase inline. */

const STEPS = [
  { id: "script",   label: "Script",          Icon: Sparkle,    color: "#C85A32" },
  { id: "image",    label: "Image + Cast",    Icon: Camera,     color: "#5B7CFA" },
  { id: "video",    label: "Video",           Icon: FilmStrip,  color: "#B89B72" },
  { id: "timeline", label: "Timeline + Plan", Icon: ListNumbers, color: "#5FB37C" },
];

export default function WorkflowComposer({ onJumpToDraft }) {
  const [mode, setMode] = useState("stepbystep"); // "stepbystep" | "oneshot"
  const [stepIdx, setStepIdx] = useState(0);
  const [registry, setRegistry] = useState(null);
  const [recos, setRecos] = useState([]);
  const [castList, setCastList] = useState([]);
  const [config, setConfig] = useState({
    prompt: "",
    scene_count: 5,
    script_model_id: "",
    image_model_id: "",
    video_model_id: "",
    voice_model_id: "",
    cast_member_ids: [],
    budget_script_inr: 50,
    budget_image_inr: 80,
    budget_video_inr: 400,
    schedule: {
      ready_by_ist: "21:00",
      publish_at_ist: "07:00",
      auto_publish_unapproved: false,
    },
  });

  useEffect(() => {
    Promise.all([
      api.get("/admin/social/models-registry").then((r) => r.data).catch(() => null),
      api.get(`/admin/social/recommendations?scene_count=${config.scene_count}`).then((r) => r.data).catch(() => null),
      api.get("/admin/social/cast").then((r) => r.data).catch(() => []),
    ]).then(([reg, rec, cast]) => {
      setRegistry(reg);
      setRecos(rec?.presets || []);
      setCastList(cast || []);
      // Seed defaults from registry if config still empty.
      if (reg) {
        const pickDefault = (step) => {
          const items = reg[step] || [];
          const def = items.find((m) => m.default && m.enabled);
          return def?.model_id || items.find((m) => m.enabled)?.model_id || "";
        };
        setConfig((c) => ({
          ...c,
          script_model_id: c.script_model_id || pickDefault("script"),
          image_model_id:  c.image_model_id  || pickDefault("image"),
          video_model_id:  c.video_model_id  || pickDefault("video"),
          voice_model_id:  c.voice_model_id  || pickDefault("voice"),
        }));
      }
    });
  }, [config.scene_count]);

  const applyPreset = (preset) => {
    setConfig((c) => ({
      ...c,
      script_model_id: preset.script.model_id,
      image_model_id: preset.image.model_id,
      video_model_id: preset.video.model_id,
    }));
    toast.success(`Applied "${preset.label}" preset (₹${preset.estimated_cost_inr})`);
  };

  return (
    <div className="space-y-5" data-testid="workflow-composer">
      {/* Mode toggle — Iter-38 */}
      <div className="card-flat p-3 flex items-center justify-between flex-wrap gap-3" data-testid="workflow-mode-toggle">
        <div className="text-xs text-muted-foreground">
          <b className="text-foreground">Composer mode:</b>{" "}
          {mode === "stepbystep"
            ? "Generate + review each phase inline (recommended)."
            : "Configure all knobs upfront, then run the full pipeline in one go."}
        </div>
        <div className="inline-flex rounded border hairline overflow-hidden">
          <button onClick={() => setMode("stepbystep")}
            data-testid="workflow-mode-stepbystep"
            className={`px-3 py-1.5 text-xs ${mode === "stepbystep" ? "bg-[hsl(var(--primary))] text-white" : "bg-background text-muted-foreground"}`}>
            Step-by-step
          </button>
          <button onClick={() => setMode("oneshot")}
            data-testid="workflow-mode-oneshot"
            className={`px-3 py-1.5 text-xs border-l hairline ${mode === "oneshot" ? "bg-[hsl(var(--primary))] text-white" : "bg-background text-muted-foreground"}`}>
            Run now (one-shot)
          </button>
        </div>
      </div>

      {mode === "stepbystep" ? (
        <StepByStepComposer onJumpToDraft={onJumpToDraft} />
      ) : (
        <OneShotComposerBody
          stepIdx={stepIdx} setStepIdx={setStepIdx}
          registry={registry} recos={recos}
          castList={castList} setCastList={setCastList}
          config={config} setConfig={setConfig}
          applyPreset={applyPreset}
        />
      )}
    </div>
  );
}

function OneShotComposerBody({ stepIdx, setStepIdx, registry, recos, castList, setCastList, config, setConfig, applyPreset }) {
  return (
    <div className="space-y-5">
      {/* Step rail */}
      <div className="card-flat p-3">
        <div className="flex items-center gap-2 flex-wrap">
          {STEPS.map((s, i) => {
            const active = i === stepIdx;
            const done = i < stepIdx;
            return (
              <button
                key={s.id}
                onClick={() => setStepIdx(i)}
                data-testid={`workflow-step-${s.id}`}
                className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs rounded
                  ${active ? "bg-[hsl(var(--primary))] text-white" : done ? "bg-[hsl(var(--muted))] text-foreground" : "text-muted-foreground hover:text-foreground"}`}
              >
                {done ? <Check size={12} weight="bold" /> : <s.Icon size={12} weight="duotone" style={{ color: active ? "white" : s.color }} />}
                {i + 1}. {s.label}
                {i < STEPS.length - 1 && <ArrowRight size={11} className="opacity-50 ml-1" />}
              </button>
            );
          })}
        </div>
      </div>

      {/* Recommendation banner (only on first 3 steps) */}
      {stepIdx < 3 && recos.length > 0 && (
        <div className="card-flat p-4" data-testid="workflow-recommendations">
          <div className="eyebrow mb-3 inline-flex items-center gap-2">
            <Lightbulb size={14} weight="duotone" className="text-[hsl(var(--secondary))]" />
            Recommended workflows
          </div>
          <div className="grid lg:grid-cols-3 gap-3">
            {recos.map((p) => (
              <button
                key={p.key}
                onClick={() => applyPreset(p)}
                data-testid={`workflow-reco-${p.key}`}
                className="text-left card-flat p-3 hover:border-[hsl(var(--primary))] border-l-2 border-transparent transition-colors"
              >
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{p.label}</div>
                <div className="font-medium text-sm mt-1">{p.headline}</div>
                <div className="text-[11px] text-muted-foreground mt-1.5 line-clamp-2">{p.rationale}</div>
                <div className="text-[11px] text-[hsl(var(--primary))] font-medium mt-2">₹{p.estimated_cost_inr} for {p.scene_count} scenes</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step body */}
      {stepIdx === 0 && (
        <StepScript registry={registry} config={config} setConfig={setConfig} />
      )}
      {stepIdx === 1 && (
        <StepImage registry={registry} config={config} setConfig={setConfig} castList={castList} setCastList={setCastList} />
      )}
      {stepIdx === 2 && (
        <StepVideo registry={registry} config={config} setConfig={setConfig} />
      )}
      {stepIdx === 3 && (
        <StepTimeline config={config} setConfig={setConfig} />
      )}

      {/* Nav buttons */}
      <div className="flex items-center justify-between" data-testid="workflow-nav">
        <button
          onClick={() => setStepIdx((i) => Math.max(0, i - 1))}
          disabled={stepIdx === 0}
          className="btn-ghost px-3 py-1.5 text-xs disabled:opacity-40"
          data-testid="workflow-back"
        >
          ← Back
        </button>
        {stepIdx < STEPS.length - 1 ? (
          <button
            onClick={() => setStepIdx((i) => Math.min(STEPS.length - 1, i + 1))}
            className="btn-primary px-4 py-2 text-xs inline-flex items-center gap-1"
            data-testid="workflow-next"
          >
            Next: {STEPS[stepIdx + 1].label} <ArrowRight size={12} weight="bold" />
          </button>
        ) : (
          <SaveAndRun config={config} />
        )}
      </div>
    </div>
  );
}

/* ---------------- Step 1: Script ---------------- */
function StepScript({ registry, config, setConfig }) {
  if (!registry) return <div className="text-sm text-muted-foreground">Loading models…</div>;
  const scripts = (registry.script || []).filter((m) => m.enabled);
  const picked = scripts.find((m) => m.model_id === config.script_model_id);
  return (
    <div className="space-y-4">
      <div className="card-flat p-5">
        <div className="eyebrow mb-2">Step 1 · Pick a script model</div>
        <p className="text-xs text-muted-foreground mb-3 max-w-2xl">
          The script writer produces scene-by-scene voice-over, image prompts, video prompts,
          per-platform captions and an SEO long-form article in one pass.
        </p>
        <div className="grid sm:grid-cols-3 gap-3" data-testid="step-script-models">
          {scripts.map((m) => (
            <ModelCard
              key={m.id}
              model={m}
              selected={m.model_id === config.script_model_id}
              onClick={() => setConfig({ ...config, script_model_id: m.model_id })}
              testid={`script-model-${m.model_id}`}
            />
          ))}
        </div>
      </div>

      <div className="card-flat p-5">
        <div className="eyebrow mb-2">Short hook / topic</div>
        <textarea
          rows={3}
          value={config.prompt}
          onChange={(e) => setConfig({ ...config, prompt: e.target.value })}
          placeholder="e.g. How a 30-something Bengaluru engineer decides between renting in Whitefield vs buying a Rs 1.2 Cr flat in Yelahanka."
          className="w-full bg-background border hairline px-3 py-2 text-sm"
          data-testid="step-script-prompt"
        />
        <div className="flex items-center gap-4 mt-3">
          <label className="text-xs">
            Scenes:{" "}
            <input
              type="number" min={2} max={10}
              value={config.scene_count}
              onChange={(e) => setConfig({ ...config, scene_count: parseInt(e.target.value, 10) || 5 })}
              className="bg-background border hairline px-2 py-1 text-xs w-16"
              data-testid="step-script-scenes"
            />
          </label>
          <BudgetField
            label="Step budget"
            value={config.budget_script_inr}
            onChange={(v) => setConfig({ ...config, budget_script_inr: v })}
            estimate={picked ? picked.cost_per_unit_inr : 0}
            testid="budget-script"
          />
        </div>
      </div>
    </div>
  );
}

/* ---------------- Step 2: Image + Cast ---------------- */
function StepImage({ registry, config, setConfig, castList, setCastList }) {
  const images = (registry?.image || []).filter((m) => m.enabled);
  const picked = images.find((m) => m.model_id === config.image_model_id);
  const sceneCostEstimate = picked ? picked.cost_per_unit_inr * config.scene_count : 0;
  return (
    <div className="space-y-4">
      <div className="card-flat p-5">
        <div className="eyebrow mb-2">Step 2 · Image model</div>
        <div className="grid sm:grid-cols-2 gap-3" data-testid="step-image-models">
          {images.map((m) => (
            <ModelCard
              key={m.id}
              model={m}
              selected={m.model_id === config.image_model_id}
              onClick={() => setConfig({ ...config, image_model_id: m.model_id })}
              perSceneScale={config.scene_count}
              testid={`image-model-${m.model_id}`}
            />
          ))}
        </div>
        <div className="mt-3">
          <BudgetField
            label="Step budget"
            value={config.budget_image_inr}
            onChange={(v) => setConfig({ ...config, budget_image_inr: v })}
            estimate={sceneCostEstimate}
            testid="budget-image"
          />
        </div>
      </div>

      <CastSection
        castList={castList}
        setCastList={setCastList}
        selectedIds={config.cast_member_ids}
        onToggle={(id) => {
          const has = config.cast_member_ids.includes(id);
          setConfig({
            ...config,
            cast_member_ids: has
              ? config.cast_member_ids.filter((x) => x !== id)
              : [...config.cast_member_ids, id],
          });
        }}
      />
    </div>
  );
}

function CastSection({ castList, setCastList, selectedIds, onToggle }) {
  const [adding, setAdding] = useState(false);
  return (
    <div className="card-flat p-5" data-testid="step-image-cast">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="eyebrow mb-1 inline-flex items-center gap-1.5">
            <Users size={14} weight="duotone" /> Cast (consistent across all scenes)
          </div>
          <p className="text-[11px] text-muted-foreground max-w-xl">
            Pick the characters that appear in every scene. Their reference image gets fed to
            the image model as the seed so the same person looks the same in every frame.
            Voice id is reserved for the Phase 2 voiceover step.
          </p>
        </div>
        <button onClick={() => setAdding(true)} className="btn-ghost px-3 py-1.5 text-xs" data-testid="cast-add-btn">
          + Add character
        </button>
      </div>
      {castList.length === 0 ? (
        <div className="text-xs text-muted-foreground py-6 text-center border hairline">
          No cast yet. Add a character so Gemini renders the same person across every scene.
        </div>
      ) : (
        <div className="grid sm:grid-cols-3 gap-3">
          {castList.map((c) => {
            const sel = selectedIds.includes(c.id);
            return (
              <button
                key={c.id}
                onClick={() => onToggle(c.id)}
                data-testid={`cast-card-${c.id}`}
                className={`text-left card-flat p-3 border-2 ${sel ? "border-[hsl(var(--primary))]" : "border-transparent hover:border-muted-foreground/30"}`}
              >
                <div className="flex items-center gap-3">
                  {c.reference_image_url ? (
                    <img src={assetUrl(c.reference_image_url)} alt={c.name} className="w-12 h-12 object-cover rounded border hairline" />
                  ) : (
                    <div className="w-12 h-12 bg-muted rounded flex items-center justify-center text-xs">
                      {(c.name || "?")[0]}
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{c.name}</div>
                    <div className="text-[10px] text-muted-foreground truncate">
                      {c.role_label || "Character"}{c.voice_label ? ` · ${c.voice_label}` : ""}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
      {adding && <AddCastModal onClose={() => setAdding(false)} onCreated={(c) => { setCastList((l) => [c, ...l]); setAdding(false); }} />}
    </div>
  );
}

function AddCastModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", role_label: "", description: "",
    voice_provider: "", voice_id: "", voice_label: "", seed: 73,
    heygen_avatar_id: "", heygen_voice_id: "",
  });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [browsing, setBrowsing] = useState(null);  // null | "avatar" | "voice"

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      // The base create endpoint doesn't accept heygen_* fields — bind them after create.
      const baseFields = ["name", "role_label", "description", "voice_provider", "voice_id", "voice_label", "seed"];
      baseFields.forEach((k) => fd.append(k, form[k]));
      if (file) fd.append("image", file);
      const { data } = await api.post("/admin/social/cast", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const newId = data.id;
      // Phase 2.D — bind HeyGen ids if the admin entered them
      if (newId && (form.heygen_avatar_id || form.heygen_voice_id)) {
        await api.put(`/admin/social/cast/${newId}/heygen-binding`, {
          heygen_avatar_id: form.heygen_avatar_id || null,
          heygen_voice_id: form.heygen_voice_id || null,
        });
      }
      const { data: list } = await api.get("/admin/social/cast-v2");
      const created = list.find((x) => x.id === newId);
      onCreated(created || { id: newId, name: form.name, role_label: form.role_label });
      toast.success("Character added");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-background border hairline max-w-lg w-full p-5 space-y-3 max-h-[90vh] overflow-y-auto" data-testid="cast-add-modal">
        <div className="eyebrow">Add character</div>
        <CastField label="Name *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="cast-name" />
        <CastField label="Role / archetype" value={form.role_label} onChange={(v) => setForm({ ...form, role_label: v })} placeholder="e.g. Bengaluru engineer, 32" />
        <CastField label="Description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} placeholder="Short prompt the image model will use" rows={2} />
        <CastField label="Voice provider" value={form.voice_provider} onChange={(v) => setForm({ ...form, voice_provider: v })} placeholder="elevenlabs / openai" />
        <CastField label="Voice id" value={form.voice_id} onChange={(v) => setForm({ ...form, voice_id: v })} placeholder="ElevenLabs voice id" />
        <CastField label="Voice label" value={form.voice_label} onChange={(v) => setForm({ ...form, voice_label: v })} placeholder="e.g. Female · Indian English · 30s" />
        <CastField label="Image seed" type="number" value={form.seed} onChange={(v) => setForm({ ...form, seed: parseInt(v, 10) || 73 })} />
        <VoicePreviewButton voiceId={form.voice_id} />

        {/* Phase 2.D — HeyGen Pro bindings */}
        <div className="border-t hairline pt-3 mt-3">
          <div className="eyebrow mb-2">HeyGen Pro bindings (optional)</div>
          <div className="grid grid-cols-2 gap-2">
            <CastField label="HeyGen avatar id" value={form.heygen_avatar_id} onChange={(v) => setForm({ ...form, heygen_avatar_id: v })} placeholder="from catalog" testid="cast-heygen-avatar" />
            <CastField label="HeyGen voice id" value={form.heygen_voice_id} onChange={(v) => setForm({ ...form, heygen_voice_id: v })} placeholder="from voice catalog" testid="cast-heygen-voice" />
          </div>
          <div className="flex gap-2 mt-2">
            <button type="button" onClick={() => setBrowsing("avatar")} className="btn-ghost px-3 py-1 text-[11px]" data-testid="cast-browse-avatars">
              Browse avatars
            </button>
            <button type="button" onClick={() => setBrowsing("voice")} className="btn-ghost px-3 py-1 text-[11px]" data-testid="cast-browse-voices">
              Browse voices
            </button>
          </div>
        </div>

        <label className="text-sm block">
          <span className="block text-xs text-muted-foreground mb-1">Reference image (5 MB max)</span>
          <input type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="cast-file" />
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="btn-ghost px-3 py-1.5 text-xs">Cancel</button>
          <button onClick={submit} disabled={busy} className="btn-primary px-4 py-1.5 text-xs disabled:opacity-50" data-testid="cast-save">
            {busy ? "Saving…" : "Save character"}
          </button>
        </div>

        {browsing && (
          <HeyGenCatalogModal
            kind={browsing}
            onPick={(id) => {
              if (browsing === "avatar") setForm((f) => ({ ...f, heygen_avatar_id: id }));
              else setForm((f) => ({ ...f, heygen_voice_id: id }));
              setBrowsing(null);
            }}
            onClose={() => setBrowsing(null)}
          />
        )}
      </div>
    </div>
  );
}

function HeyGenCatalogModal({ kind, onPick, onClose }) {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("");
  useEffect(() => {
    const path = kind === "avatar" ? "/admin/social/heygen/avatars" : "/admin/social/heygen/voices";
    api.get(path)
      .then(({ data }) => setItems(kind === "avatar" ? data.avatars : data.voices))
      .catch((e) => {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
        setItems([]);
      });
  }, [kind]);
  const filtered = (items || []).filter((it) => {
    if (!filter) return true;
    const blob = `${it.name} ${it.language || ""} ${it.gender || ""}`.toLowerCase();
    return blob.includes(filter.toLowerCase());
  });
  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-background border hairline max-w-3xl w-full p-5 space-y-3 max-h-[90vh] overflow-y-auto" data-testid={`heygen-catalog-${kind}`}>
        <div className="flex items-center justify-between gap-2">
          <div className="eyebrow capitalize">HeyGen {kind} catalog</div>
          <input
            placeholder="Search by name / language / gender"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="bg-background border hairline px-2 py-1 text-xs w-72"
            data-testid="heygen-catalog-search"
          />
        </div>
        {items === null ? (
          <div className="text-xs text-muted-foreground py-8 text-center">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="text-xs text-muted-foreground py-8 text-center">
            {items.length === 0 ? "No items returned. Confirm the HeyGen Pro API key has access." : "No matches for that filter."}
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-3" data-testid="heygen-catalog-grid">
            {filtered.slice(0, 60).map((it) => (
              <button
                key={it.id}
                onClick={() => onPick(it.id)}
                className="text-left card-flat p-2 hover:border-[hsl(var(--primary))] border-l-2 border-transparent transition-colors"
                data-testid={`heygen-catalog-pick-${it.id}`}
              >
                {it.thumbnail_url && (
                  <img src={it.thumbnail_url} alt={it.name} className="w-full h-20 object-cover border hairline mb-2" />
                )}
                {it.preview_url && (
                  <audio controls src={it.preview_url} className="w-full h-7 mb-1" />
                )}
                <div className="text-xs font-medium truncate">{it.name}</div>
                <div className="text-[10px] text-muted-foreground truncate">
                  {[it.language, it.gender].filter(Boolean).join(" · ") || it.id}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CastField({ label, value, onChange, placeholder = "", rows, type = "text", testid }) {
  return (
    <label className="text-sm block">
      <span className="block text-xs text-muted-foreground mb-1">{label}</span>
      {rows ? (
        <textarea rows={rows} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full bg-background border hairline px-2 py-1.5 text-sm" data-testid={testid} />
      ) : (
        <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full bg-background border hairline px-2 py-1.5 text-sm" data-testid={testid} />
      )}
    </label>
  );
}

function VoicePreviewButton({ voiceId }) {
  const [busy, setBusy] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const preview = async () => {
    if (!voiceId?.trim()) { toast.error("Paste a voice_id first"); return; }
    setBusy(true); setAudioUrl(null);
    try {
      // Hit the preview endpoint with axios to get a Blob.
      const { data } = await api.post(
        "/admin/social/voice/preview",
        { voice_id: voiceId.trim() },
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(data);
      setAudioUrl(url);
      const a = new Audio(url);
      a.play().catch(() => {});
    } catch (e) {
      const msg = e.response?.data instanceof Blob
        ? "ElevenLabs preview failed"
        : formatApiErrorDetail(e.response?.data?.detail);
      toast.error(msg);
    } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-2 pt-1" data-testid="voice-preview">
      <button
        type="button"
        onClick={preview}
        disabled={busy || !voiceId?.trim()}
        className="btn-ghost px-3 py-1.5 text-xs inline-flex items-center gap-1 disabled:opacity-40"
        data-testid="voice-preview-btn"
      >
        🔊 {busy ? "Synthesising…" : "Preview voice (5 sec sample)"}
      </button>
      {audioUrl && (
        <audio controls src={audioUrl} className="h-8 max-w-xs" data-testid="voice-preview-audio" />
      )}
    </div>
  );
}

/* ---------------- Step 3: Video ---------------- */
function StepVideo({ registry, config, setConfig }) {
  const videos = (registry?.video || []).filter((m) => m.enabled);
  const picked = videos.find((m) => m.model_id === config.video_model_id);
  const estimate = picked ? picked.cost_per_unit_inr * config.scene_count : 0;
  return (
    <div className="space-y-4">
      <div className="card-flat p-5">
        <div className="eyebrow mb-2">Step 3 · Video model</div>
        <p className="text-[11px] text-muted-foreground mb-3 max-w-2xl">
          Video is the most expensive step. The model receives each scene's video prompt + the
          per-scene image you generated in Step 2 (passed as the first frame for visual continuity).
        </p>
        <div className="grid sm:grid-cols-2 gap-3" data-testid="step-video-models">
          {videos.map((m) => (
            <ModelCard
              key={m.id}
              model={m}
              selected={m.model_id === config.video_model_id}
              onClick={() => setConfig({ ...config, video_model_id: m.model_id })}
              perSceneScale={config.scene_count}
              testid={`video-model-${m.model_id}`}
            />
          ))}
        </div>
        <div className="mt-3">
          <BudgetField
            label="Step budget"
            value={config.budget_video_inr}
            onChange={(v) => setConfig({ ...config, budget_video_inr: v })}
            estimate={estimate}
            testid="budget-video"
          />
        </div>
      </div>
    </div>
  );
}

/* ---------------- Step 4: Timeline + Schedule ---------------- */
function StepTimeline({ config, setConfig }) {
  // v1: placeholder for the drag-drop timeline (the real reorder UI surfaces
  // AFTER the wizard kicks off a run and scenes exist). We focus this step on
  // the scheduling + override controls which the user explicitly asked for.
  const setSchedule = (patch) => setConfig({ ...config, schedule: { ...config.schedule, ...patch } });
  return (
    <div className="space-y-4">
      <div className="card-flat p-5">
        <div className="eyebrow mb-2 inline-flex items-center gap-1.5">
          <ListNumbers size={14} weight="duotone" /> Timeline (drag-drop after first run)
        </div>
        <p className="text-[11px] text-muted-foreground max-w-2xl mb-3">
          Once the workflow runs and produces per-scene clips, the workspace below the wizard
          will show drag-and-drop reorder + include/exclude per scene. Trim, text overlay, and
          music drop-zones land in Phase 2.
        </p>
        <div className="border hairline border-dashed p-6 text-center text-xs text-muted-foreground" data-testid="timeline-placeholder">
          <MusicNote size={28} weight="duotone" className="mx-auto mb-2 opacity-50" />
          Reorder + exclude controls surface in the draft view after generation.
        </div>
      </div>

      <div className="card-flat p-5" data-testid="step-timeline-schedule">
        <div className="eyebrow mb-2 inline-flex items-center gap-1.5">
          <Clock size={14} weight="duotone" /> Schedule
        </div>
        <p className="text-[11px] text-muted-foreground max-w-2xl mb-3">
          Workflow tries to produce a finished draft by <b>ready_by_ist</b>. The next morning at
          <b> publish_at_ist</b>, the publish cron auto-posts every <b>approved</b> draft.
          Unapproved drafts stay in the queue with an email digest — unless you flip the override below.
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          <label className="text-sm block">
            <span className="block text-xs text-muted-foreground mb-1">Ready by (IST, 24h)</span>
            <input
              type="time"
              value={config.schedule.ready_by_ist}
              onChange={(e) => setSchedule({ ready_by_ist: e.target.value })}
              className="bg-background border hairline px-3 py-2 text-sm"
              data-testid="schedule-ready-by"
            />
          </label>
          <label className="text-sm block">
            <span className="block text-xs text-muted-foreground mb-1">Publish at (next-day IST, 24h)</span>
            <input
              type="time"
              value={config.schedule.publish_at_ist}
              onChange={(e) => setSchedule({ publish_at_ist: e.target.value })}
              className="bg-background border hairline px-3 py-2 text-sm"
              data-testid="schedule-publish-at"
            />
          </label>
        </div>

        <label
          className={`mt-4 flex items-start gap-2 p-3 rounded cursor-pointer border-2
            ${config.schedule.auto_publish_unapproved ? "border-destructive bg-destructive/5" : "border-transparent bg-muted/20"}`}
          data-testid="schedule-auto-publish-toggle"
        >
          <input
            type="checkbox"
            checked={config.schedule.auto_publish_unapproved}
            onChange={(e) => setSchedule({ auto_publish_unapproved: e.target.checked })}
            className="mt-0.5"
          />
          <div>
            <div className="text-xs font-medium">
              {config.schedule.auto_publish_unapproved
                ? "⚠ Override active — unapproved drafts WILL auto-publish"
                : "Default: only approved drafts publish"}
            </div>
            <div className="text-[10px] text-muted-foreground mt-1 max-w-xl">
              {config.schedule.auto_publish_unapproved
                ? "This workflow's setting overrides the global default. The 7am cron will publish whatever's ready, approved or not. Disable for safety."
                : "Toggle on if you want hands-off auto-publish for this workflow only. The global default stays safe."}
            </div>
          </div>
        </label>
      </div>
    </div>
  );
}

/* ---------------- Save & Run ---------------- */
function SaveAndRun({ config }) {
  const [busy, setBusy] = useState(false);
  const [savedName, setSavedName] = useState("");
  const totalEst = (config.budget_script_inr || 0) + (config.budget_image_inr || 0) + (config.budget_video_inr || 0);

  const save = async () => {
    const name = savedName || `Workflow ${new Date().toISOString().slice(0, 16)}`;
    setBusy(true);
    try {
      await api.post("/admin/social/workflows", { ...config, name });
      toast.success(`Saved workflow "${name}".`);
      setSavedName("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };
  const run = async () => {
    if (config.prompt.trim().length < 4) { toast.error("Type a short prompt in Step 1 first."); return; }
    setBusy(true);
    try {
      // Phase 2.A — route through the registry-aware workflow runner so the
      // admin's model picks + cast actually drive the generation.
      const { data } = await api.post("/admin/social/workflow-run", {
        prompt: config.prompt,
        scene_count: config.scene_count,
        script_model_id: config.script_model_id,
        image_model_id: config.image_model_id,
        video_model_id: config.video_model_id,
        cast_member_ids: config.cast_member_ids,
        auto_generate_assets: true,
      });
      toast.success(
        `Workflow running · draft ${data.draft_id?.slice(-6)} · ${data.script_model_id} → ${data.image_model_id} → ${data.video_model_id}`
      );
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-3 flex-wrap" data-testid="workflow-save-run">
      <input
        value={savedName}
        onChange={(e) => setSavedName(e.target.value)}
        placeholder="Save as… (workflow template name)"
        className="bg-background border hairline px-3 py-2 text-xs w-64"
        data-testid="workflow-name"
      />
      <button onClick={save} disabled={busy} className="btn-ghost px-3 py-2 text-xs inline-flex items-center gap-1" data-testid="workflow-save">
        <FloppyDisk size={12} weight="duotone" /> Save template
      </button>
      <button onClick={run} disabled={busy} className="btn-primary px-4 py-2 text-xs inline-flex items-center gap-1.5" data-testid="workflow-run">
        <Sparkle size={12} weight="duotone" />
        {busy ? "Running…" : `Run now · ₹${totalEst}`}
      </button>
    </div>
  );
}

/* ---------------- Reusable bits ---------------- */
function ModelCard({ model, selected, onClick, perSceneScale = 1, testid }) {
  const total = model.cost_per_unit_inr * perSceneScale;
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`text-left card-flat p-3 border-2 transition-colors
        ${selected ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5" : "border-transparent hover:border-muted-foreground/30"}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="text-sm font-medium">{model.label}</div>
        {model.badge && (
          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[hsl(var(--secondary))]/15 text-[hsl(var(--secondary))]">
            {model.badge}
          </span>
        )}
      </div>
      <div className="text-[10px] text-muted-foreground mt-1">{model.provider} · {model.model_id}</div>
      <div className="text-[11px] text-[hsl(var(--primary))] font-medium mt-2 inline-flex items-center gap-0.5">
        <CurrencyInr size={11} weight="bold" />{model.cost_per_unit_inr}/{model.unit}
        {perSceneScale > 1 && (
          <span className="text-muted-foreground font-normal ml-2">≈ ₹{total} for {perSceneScale} scenes</span>
        )}
      </div>
      {model.requires_provider_key && (
        <div className="text-[10px] text-amber-600 mt-1">Requires {model.requires_provider_key} API key</div>
      )}
    </button>
  );
}

function BudgetField({ label, value, onChange, estimate, testid }) {
  const overBudget = estimate > value;
  return (
    <div className="inline-flex items-center gap-2 text-xs" data-testid={testid}>
      <span className="text-muted-foreground">{label}: ₹</span>
      <input
        type="number"
        min={0}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        className="bg-background border hairline px-2 py-1 text-xs w-24"
      />
      <span className={`text-[10px] ${overBudget ? "text-destructive" : "text-muted-foreground"}`}>
        (est. ₹{estimate}{overBudget ? " — exceeds budget" : ""})
      </span>
    </div>
  );
}
